import { useState, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"

import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { SeriesScopeDialog } from "../components/SeriesScopeDialog"
import { useMeetingsStore } from "../store/meetings.store"
import { deleteMeetingCopy, type SeriesScope } from "../utils/recurrence"

interface ScopeActionMeeting {
  id: string
  title: string
  startTimeMs: number
  series?: unknown
}

/**
 * Edit and Delete for a meeting's details screen. A one-off meeting edits
 * straight away and deletes through the screen's own confirmation; a day of a
 * series first asks "This occurrence" or "All occurrences".
 *
 * Shared by the details page and the dashboard's details panel, so both ask
 * the same question and say the same thing afterwards.
 */
export function useSeriesScopeActions(
  meeting: ScopeActionMeeting | null | undefined,
  opts: { onDeleteOneOff: () => void; onDeleted: () => void },
): { edit: () => void; remove: () => void; dialog: ReactNode } {
  const { t, language } = useLanguage()
  const navigate = useNavigate()
  const deleteMeeting = useMeetingsStore((s) => s.deleteMeeting)
  const [mode, setMode] = useState<"edit" | "delete" | null>(null)
  const [busy, setBusy] = useState(false)

  const editPath = (scope: SeriesScope) =>
    meeting ? `/schedule?edit=${encodeURIComponent(meeting.id)}${scope === "all" ? "&scope=all" : ""}` : "/meetings"

  const edit = () => (meeting?.series ? setMode("edit") : navigate(editPath("one")))
  const remove = () => (meeting?.series ? setMode("delete") : opts.onDeleteOneOff())

  const confirm = async (scope: SeriesScope) => {
    if (!meeting) return
    if (mode === "edit") {
      setMode(null)
      navigate(editPath(scope))
      return
    }
    setBusy(true)
    try {
      await deleteMeeting(meeting.id, scope)
      const copy = deleteMeetingCopy(meeting, t, language, scope)
      toast.success({ title: copy.doneTitle, description: copy.doneDescription })
      setMode(null)
      opts.onDeleted()
    } catch {
      toast.error({ title: t("meetings.couldNotDeleteMeeting") })
    } finally {
      setBusy(false)
    }
  }

  const dialog =
    meeting?.series && mode ? (
      <SeriesScopeDialog
        open
        onOpenChange={(open) => !open && !busy && setMode(null)}
        mode={mode}
        onConfirm={(scope) => void confirm(scope)}
        busy={busy}
      />
    ) : null

  return { edit, remove, dialog }
}
